import { useEffect, useMemo, useRef, useState } from 'react';
import { Puzzle } from '../../shared/types';
import { GameState, ValidationResult, applyClear, applyHint, applyUndo, computeAutoXs, createGameState, validate } from '../game';
import { formatTime } from '../utils';
import { Board } from './Board';
import { HintsPanel } from './HintsPanel';
import { computeHints, Hint, HintHighlight } from '../hints';

interface Props {
  /** The board to play, exactly as painted. Never sent anywhere. */
  regions: string[][];
  /** Back where this was opened from, with everything there still in place. */
  onExit: () => void;
  /** Where "back" goes, for the button labels. The editor is the original caller. */
  backLabel?: string;
  /**
   * Somewhere to go from the finish screen — the speed drill hands over the next board that beat
   * the player in that session, so a review of a run is one click per board rather than a trip
   * back through the summary each time. Absent for a one-off board.
   */
  onNext?: () => void;
  nextLabel?: string;
}

/**
 * The editor's "try it before publishing" board.
 *
 * A full game — timer, Auto Xs, hints — on a puzzle that does not exist server-side: there is no
 * completion form, no leaderboard and no replay, so playing one neither publishes the puzzle nor
 * touches the player's record. That also makes it the way to play any board that isn't a saved
 * puzzle without either of those side effects — a generated 6×6 for one technique, or a board the
 * speed drill just handed you and you want to sit down with (`backLabel` names the way back).
 *
 * Assists are never locked here. The lock on the newest puzzle exists to protect a leaderboard
 * this board has none of.
 */
export function PracticeView({ regions, onExit, backLabel = 'Editor', onNext, nextLabel = 'Next board' }: Props) {
  // Shaped like a Puzzle so Board/hints take it unchanged; the id is never used for anything.
  const puzzle = useMemo<Puzzle>(() => ({
    id: -1,
    name: 'Practice',
    size: regions.length,
    colorRegions: regions,
  }), [regions]);

  const [gameState, setGameState] = useState<GameState>(() => createGameState(puzzle));
  const [timerDisplay, setTimerDisplay] = useState('00:00');
  const [timerRunning, setTimerRunning] = useState(true);
  const [solvedMs, setSolvedMs] = useState<number | null>(null);
  // Same preferences the real game uses, so a player's setup carries over both ways.
  const [autoXsEnabled, setAutoXsEnabled] = useState(() => localStorage.getItem('autoXs') === '1');
  const [hintsEnabled, setHintsEnabled] = useState(() => localStorage.getItem('hints') === '1');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hintHighlight, setHintHighlight] = useState<HintHighlight | null>(null);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    if (!timerRunning) return;
    const id = setInterval(() => {
      setTimerDisplay(formatTime(Date.now() - startTimeRef.current));
    }, 100);
    return () => clearInterval(id);
  }, [timerRunning]);

  function handleBoardChange(newState: GameState, result: ValidationResult) {
    setGameState(newState);
    if (result.solved) {
      setSolvedMs(Date.now() - startTimeRef.current);
      setTimerRunning(false);
    }
  }

  function handleApplyHint(h: Hint) {
    const newState = applyHint(gameState, h.kind as 'eliminate' | 'place', h.cells, puzzle.colorRegions, h.rule);
    handleBoardChange(newState, validate(newState.board, puzzle.colorRegions));
  }

  function handleRestart() {
    setGameState(createGameState(puzzle));
    setHintHighlight(null);
    setSolvedMs(null);
    startTimeRef.current = Date.now();
    setTimerDisplay('00:00');
    setTimerRunning(true);
    setSettingsOpen(false);
  }

  function handleToggleAutoXs() {
    const next = !autoXsEnabled;
    setAutoXsEnabled(next);
    localStorage.setItem('autoXs', next ? '1' : '0');
  }

  function handleToggleHints() {
    const next = !hintsEnabled;
    setHintsEnabled(next);
    localStorage.setItem('hints', next ? '1' : '0');
    if (!next) setHintHighlight(null);
  }

  const showHints = hintsEnabled && solvedMs === null;
  const hints = useMemo(
    () => {
      if (!showHints) return [];
      const visibleXs = autoXsEnabled ? computeAutoXs(gameState.board, puzzle.colorRegions) : undefined;
      return computeHints(gameState.board, puzzle.colorRegions, visibleXs);
    },
    [showHints, puzzle, gameState, autoXsEnabled]
  );

  useEffect(() => { if (!showHints) setHintHighlight(null); }, [showHints]);

  return (
    <div id="view-practice">
      <header className="game-header">
        <div className="game-header-inner">
          <button className="btn-back" onClick={onExit}>←<span className="hide-mobile"> {backLabel}</span></button>
          <h2>
            <span className="game-title-name">Practice board</span>
            <span className="multi-tag" title="Nothing here is saved: no leaderboard entry and no effect on your record">
              not saved
            </span>
          </h2>
          <div className="game-nav">
            <button className="btn-nav" onClick={handleRestart}><span className="show-mobile">↺</span><span className="hide-mobile">Restart</span></button>
          </div>
        </div>
      </header>
      <main>
        <div className="board-wrapper">
          {solvedMs !== null && (
            <div className="completion-panel">
              <h3>Puzzle solved!</h3>
              <p className="completion-time">Time: <strong>{formatTime(solvedMs)}</strong></p>
              <p className="settings-note">A practice run goes nowhere — no leaderboard entry, nothing added to your record.</p>
              {/* When there is somewhere to go next, that is the action; otherwise the way out is.
                  Replaying a board you have just solved is the alternative either way. */}
              <div className="completion-row">
                {onNext && <button className="btn-primary" onClick={onNext}>{nextLabel}</button>}
                <button className={onNext ? 'btn-secondary' : 'btn-primary'} onClick={onExit}>
                  Back to {backLabel}
                </button>
                <button className="btn-secondary" onClick={handleRestart}>Play again</button>
              </div>
            </div>
          )}
          <div className="board-top-bar">
            <span className="timer">{timerDisplay}</span>
            <div className="board-actions">
              <button
                className={`btn-icon${settingsOpen ? ' btn-icon-on' : ''}`}
                onClick={() => setSettingsOpen(o => !o)}
              >Help</button>
              <button className="btn-icon" onClick={() => setGameState(applyUndo(gameState))}>↩ Undo</button>
              <button className="btn-icon" onClick={() => setGameState(applyClear(gameState))}>✕ Clear</button>
            </div>
          </div>
          <Board
            puzzle={puzzle}
            state={gameState}
            onStateChange={handleBoardChange}
            autoXs={autoXsEnabled}
            highlight={hintHighlight}
          />
          {showHints && (
            <HintsPanel
              hints={hints}
              onHighlight={setHintHighlight}
              onUse={() => {}}
              onApply={handleApplyHint}
            />
          )}
        </div>
      </main>
      {settingsOpen && (
        <div className="help-modal-overlay" onClick={() => setSettingsOpen(false)}>
          <div className="help-modal" onClick={e => e.stopPropagation()}>
            <h3>Settings</h3>
            <label className="toggle-row">
              <span>Auto Xs</span>
              <span className="toggle-switch">
                <input type="checkbox" checked={autoXsEnabled} onChange={handleToggleAutoXs} />
                <span className="toggle-slider" />
              </span>
            </label>
            <label className="toggle-row">
              <span>Hints</span>
              <span className="toggle-switch">
                <input type="checkbox" checked={hintsEnabled} onChange={handleToggleHints} />
                <span className="toggle-slider" />
              </span>
            </label>
            <button className="settings-row" onClick={() => { setSettingsOpen(false); onExit(); }}>Back to {backLabel}</button>
            <button className="btn-secondary" style={{ marginTop: '0.25rem' }} onClick={() => setSettingsOpen(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
